import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ImportedEvent, Venue } from "@myams/shared";
import {
  MaBottomNav, MaChipRow, MaIcon, MaSectionHeader, MaVenueCard,
} from "../components/ui";
import { Screen } from "../components/layout/Screen";
import { useImportedEvents, useVenues } from "../lib/queries";
import { formatEventDateShort } from "../lib/time";

const RECENTS_KEY = "myams.search.recents";

type Filter = "all" | "open" | "located" | "music" | "art" | "outdoor" | "food";

function loadRecents(): string[] {
  if (typeof localStorage === "undefined") return [];
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? "[]"); } catch { return []; }
}
function saveRecent(q: string) {
  if (typeof localStorage === "undefined" || !q.trim()) return;
  try {
    const cur = loadRecents().filter((r) => r.toLowerCase() !== q.toLowerCase());
    const next = [q, ...cur].slice(0, 8);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

export function Search() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [recents, setRecents] = useState<string[]>(() => loadRecents());

  // Debounce the query so we don't hammer the API on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const { data: venueResults = [] } = useVenues({ q: debouncedQ || undefined });
  // `upcoming: true` filters out events whose end (or start, for single-
  // day) is already in the past — fixes the bug where months-stale events
  // were showing up in search. See apps/api/src/routes/imports.ts.
  const { data: eventResults = [] } = useImportedEvents({ upcoming: true, limit: 100 });

  // Combined search across venues + imported events
  const results = useMemo(() => {
    const needle = debouncedQ.toLowerCase();
    let venues = venueResults;
    let events = eventResults;
    if (needle) {
      const test = (s: string | null | undefined) => (s ?? "").toLowerCase().includes(needle);
      venues = venues.filter((v) => test(v.name) || test(v.neigh) || test(v.cat));
      events = events.filter((e) => test(e.title) || test(e.description) || test(e.neighborhood) || test(e.category ?? ""));
    }
    if (filter === "open")    { venues = venues.filter((v) => v.live); }
    if (filter === "located") { venues = venues.filter((v) => v.lat != null); events = events.filter((e) => e.lat != null); }
    if (filter === "music" || filter === "art" || filter === "outdoor" || filter === "food") {
      venues = venues.filter((v) => v.cat === filter);
      events = events.filter((e) => e.category === filter);
    }
    return { venues, events };
  }, [debouncedQ, filter, venueResults, eventResults]);

  const totalCount = results.venues.length + results.events.length;
  // Visible event slice — same .slice(0, 8) the list uses. Capturing it
  // here so the siblings array passed to /event/:id matches exactly what
  // the user could swipe through.
  const visibleEvents = results.events.slice(0, 8);
  const siblingIds = visibleEvents.map((e) => e.id);

  const commitRecent = () => {
    if (debouncedQ) { saveRecent(debouncedQ); setRecents(loadRecents()); }
  };

  return (
    <Screen footer={<MaBottomNav active="search" />}>
      {/* Sticky header so scroll-memory restoration on Back doesn't strand
          the search bar off-screen. Background matches paper so the
          underlying scroll doesn't bleed through. */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        padding: "calc(env(safe-area-inset-top, 20px) + 34px) 14px 10px",
        background: "var(--ma-paper)",
      }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 10,
            padding: "12px 14px",
            background: "var(--ma-cream)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
          }}>
            <MaIcon name="search" size={18} stroke={1.8} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onBlur={commitRecent}
              onKeyDown={(e) => { if (e.key === "Enter") commitRecent(); }}
              placeholder="Search venues, events, neighbourhoods"
              style={{
                flex: 1, border: "none", outline: "none",
                background: "transparent", fontFamily: "var(--ma-body)",
                // 16px so iOS Safari doesn't auto-zoom on focus.
                fontSize: 16,
                color: "var(--ma-ink)", minWidth: 0,
              }}
            />
            {q && (
              <button onClick={() => setQ("")} aria-label="Clear" style={{
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                color: "var(--ma-ink-soft)",
              }}>
                <MaIcon name="close" size={14} stroke={2} />
              </button>
            )}
            <span className="ma-mono" style={{ fontSize: 10, color: "var(--ma-ink-soft)", whiteSpace: "nowrap" }}>
              {totalCount} results
            </span>
          </div>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: 0, background: "transparent", border: "none",
              color: "var(--ma-ink)", fontFamily: "var(--ma-mono)", fontSize: 12,
              letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>

        <div style={{ marginTop: 10, marginLeft: -14, marginRight: -14 }}>
          <MaChipRow
            items={[
              { id: "all",     label: "All" },
              { id: "open",    label: "Open now", live: filter === "open" },
              { id: "located", label: "On map" },
              { id: "music",   label: "Music" },
              { id: "art",     label: "Art" },
              { id: "food",    label: "Food" },
              { id: "outdoor", label: "Outdoor" },
            ]}
            active={filter}
            onSelect={(id) => setFilter(id as Filter)}
          />
        </div>
      </div>

      <div style={{ padding: "6px 16px 24px" }}>
        {results.venues.length > 0 && (
          <>
            <MaSectionHeader label={`Venues · ${results.venues.length}`} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {results.venues.slice(0, 8).map((v: Venue) => (
                <MaVenueCard key={v.id} venue={v} variant="mini" onClick={() => navigate(`/venue/${v.id}`)} />
              ))}
            </div>
          </>
        )}

        {results.events.length > 0 && (
          <>
            <MaSectionHeader label={`Events · ${results.events.length}`} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {visibleEvents.map((e: ImportedEvent, i) => (
                <button
                  key={e.id}
                  onClick={() => navigate(`/event/${encodeURIComponent(e.id)}`, {
                    // Pass the in-view list so EventDetail can support
                    // swipe between siblings without losing back-to-list.
                    state: { siblings: siblingIds, index: i },
                  })}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: 10,
                    background: "var(--ma-cream)",
                    border: "var(--ma-border) solid var(--ma-ink)",
                    borderRadius: "var(--ma-card-radius)",
                    color: "inherit", cursor: "pointer", width: "100%", textAlign: "left",
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ma-ink)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.title}
                    </div>
                    <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em",
                      textTransform: "uppercase", color: "var(--ma-ink-soft)", marginTop: 2,
                      display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {e.startsAt && (
                        <span style={{ color: "var(--ma-ink)" }}>
                          {formatEventDateShort(e.startsAt, e.endsAt ?? null)}
                        </span>
                      )}
                      <span>{e.category ?? ""}{e.neighborhood ? ` · ${e.neighborhood}` : ""}</span>
                    </div>
                  </div>
                  <MaIcon name="arrow" size={14} stroke={1.6} color="var(--ma-ink-soft)" />
                </button>
              ))}
            </div>
          </>
        )}

        {totalCount === 0 && debouncedQ && (
          <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", padding: 24, textAlign: "center", letterSpacing: "0.1em" }}>
            no matches for "{debouncedQ}"
          </div>
        )}

        {recents.length > 0 && (
          <>
            <MaSectionHeader
              label="Recent searches"
              action="Clear"
              onAction={() => { localStorage.removeItem(RECENTS_KEY); setRecents([]); }}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              {recents.map((r) => (
                <button key={r} className="ma-chip" onClick={() => setQ(r)} style={{ cursor: "pointer" }}>
                  <MaIcon name="clock" size={11} stroke={1.8} /> {r}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
